import Button from '@material-ui/core/Button'
import Typography from '@material-ui/core/Typography'
import { Done } from '@material-ui/icons'
import cx from 'clsx'
import { useEffect, useState } from 'react'

import { toSafeUrl } from '@karakuri-web/common/utils'

import { uniqueKey } from '../../utils'

import { buildAnswerHref } from './buildAnswerLink'
import { getOperatorEscalationUrlForAction } from './getOperatorEscalationUrl'
import { AnswerActionProps } from './interface'

/**
 * 選択肢ボタンのラベル末尾に付ける改行。
 * 隣接するボタンはHTMLソース上で区切りなく連続するため、外部生成AIがテキスト抽出した際に
 * ラベル同士が連結して認識される（例:「料金」「最低利用期間」→「料金最低利用期間」）。
 * ボタン内部は flex / block レイアウトで末尾の空白文字は描画されないため、見た目は変わらない。
 */
const ACTION_LABEL_SEPARATOR = '\n'

export function AnswerActions({
  className,
  cardId,
  pathPrefix,
  actions,
  selectionPath,
  operatorEscalationKeyword,
  onActionSelected,
  karakuriWebRootUrl,
  session,
}: AnswerActionProps) {
  const [operatorEscalationUrl, setOperatorEscalationUrl] = useState<string>('')

  useEffect(() => {
    actions?.actions?.forEach(action => {
      if (
        action &&
        !action.invalid &&
        (action.label?.includes(operatorEscalationKeyword) || action.isOperatorConnect)
      ) {
        const generatedUrl = getOperatorEscalationUrlForAction({
          action,
          karakuriWebRootUrl,
          operatorEscalationKeyword,
          session,
        })
        setOperatorEscalationUrl(generatedUrl)
      }
    })
  }, [actions, karakuriWebRootUrl, operatorEscalationKeyword, session])

  return actions && actions.actions ? (
    <div className="faq-answer-actions-wrapper">
      {actions.actions.map((action, index) => {
        if (!action || action.invalid) {
          return null
        }

        const id = action.id || ''
        if (action.label?.includes(operatorEscalationKeyword) || action.isOperatorConnect) {
          return (
            <Button
              key={uniqueKey('button', cardId, id, action.id || '', index)}
              fullWidth
              variant="contained"
              color="secondary"
              className={cx('faq-button', 'faq-button-operator', className)}
              href={operatorEscalationUrl}
              target="chat"
              rel="noopener noreferrer"
            >
              {action.label}
              {ACTION_LABEL_SEPARATOR}
            </Button>
          )
        }

        // 会話カードの回答は一般ユーザーも編集できるため、javascript: 等のURLはリンクにしない
        const uri = toSafeUrl(action.uri)
        if (uri) {
          return (
            <Button
              key={uniqueKey('button', cardId, id, action.id || '', index)}
              fullWidth
              variant="contained"
              color="secondary"
              className={cx('faq-button', 'faq-button-uri', className)}
              href={uri}
              target="_blank"
              rel="noopener"
            >
              {action.label}
              {ACTION_LABEL_SEPARATOR}
            </Button>
          )
        }

        if (action.external) {
          return (
            <Button
              key={uniqueKey('button', cardId, id, action.id || '', index)}
              fullWidth
              variant="contained"
              color="secondary"
              className={cx(['faq-button', 'faq-button-external', className])}
              href={id ? buildAnswerHref(id, pathPrefix) : undefined}
              onClick={e => onActionSelected(e, action.id, !!action.external, action.uri)}
            >
              <Typography color="inherit" variant="inherit">
                {action.label}
              </Typography>
              {ACTION_LABEL_SEPARATOR}
            </Button>
          )
        }

        const selected = selectionPath.includes(id)
        const unselected =
          !selected && (actions?.actions || []).some(x => x && selectionPath.includes(x.id))
        return (
          <Button
            key={uniqueKey('button', cardId, id, action.id || '', index)}
            fullWidth
            variant="contained"
            color="secondary"
            className={cx(
              'faq-button',
              'faq-button-answer',
              {
                'faq-button-unselected': unselected,
                'faq-button-selected': selected,
              },
              className
            )}
            onClick={e => onActionSelected(e, action.id, !!action.external, action.uri)}
          >
            <Typography color={unselected ? 'textSecondary' : 'inherit'} variant="inherit">
              {action.label}
              {selected ? <Done fontSize="small" className="faq-button-selected-icon" /> : null}
            </Typography>
            {ACTION_LABEL_SEPARATOR}
          </Button>
        )
      })}
    </div>
  ) : null
}
